import { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ArrowRight, Briefcase, MapPin } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import { ROLES } from "@/data/site";
import { api } from "@/lib/api";

export default function Careers() {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [submitting, setSubmitting] = useState(false);
  const [selectedRole, setSelectedRole] = useState(ROLES[0].title);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await api.post("/careers/apply", { ...data, role: selectedRole });
      toast.success("Application received — we'll be in touch.");
      reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="careers-page">
      <PageHeader
        overline="Careers"
        title="Build the products and platforms that operators rely on"
        subtitle="We hire senior people who care deeply about craft, outcomes and the businesses they serve. Two hubs — Mumbai and Dubai — with flexible work models."
        testid="careers-page-header"
      />

      <section className="py-12 md:py-16">
        <div className="container-zx grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="overline mb-3">Open Roles</div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-white">Currently hiring</h2>
            <div className="mt-6 space-y-3">
              {ROLES.map((r) => (
                <button
                  key={r.title}
                  onClick={() => setSelectedRole(r.title)}
                  data-testid={`role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={
                    "w-full text-left card-surface p-5 flex items-center justify-between transition " +
                    (selectedRole === r.title ? "border-cyan/40 shadow-glow" : "")
                  }
                >
                  <div>
                    <div className="text-sm font-semibold text-white">{r.title}</div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-ink-400">
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {r.location}</span>
                      <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {r.type}</span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-ink-400" />
                </button>
              ))}
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm text-ink-200">
              {[
                ["Culture", "Senior. Honest. High-output."],
                ["Growth", "Real ownership and a clear path."],
                ["Impact", "Ship to operators who use it daily."],
              ].map(([t, d]) => (
                <div key={t} className="card-surface p-5">
                  <div className="text-white font-semibold">{t}</div>
                  <div className="mt-1 text-xs text-ink-400">{d}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <motion.form
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              onSubmit={handleSubmit(onSubmit)}
              className="card-surface p-7"
              data-testid="careers-form"
            >
              <div className="overline mb-2">Apply</div>
              <h3 className="font-display text-xl font-semibold text-white">For: {selectedRole}</h3>

              <div className="mt-6 space-y-4">
                <Field label="Full name" error={errors.name?.message} testid="careers-name">
                  <input {...register("name", { required: "Required" })} className={inputCls} placeholder="Your name" />
                </Field>
                <Field label="Email" error={errors.email?.message} testid="careers-email">
                  <input type="email" {...register("email", { required: "Required" })} className={inputCls} placeholder="you@email.com" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" testid="careers-phone">
                    <input {...register("phone")} className={inputCls} placeholder="Optional" />
                  </Field>
                  <Field label="Location" testid="careers-location">
                    <input {...register("location")} className={inputCls} placeholder="City, Country" />
                  </Field>
                </div>
                <Field label="Portfolio / LinkedIn URL" testid="careers-portfolio">
                  <input {...register("portfolio_url")} className={inputCls} placeholder="https://" />
                </Field>
                <Field label="Cover letter" error={errors.cover_letter?.message} testid="careers-cover">
                  <textarea rows={5} {...register("cover_letter", { required: "Required", minLength: { value: 5, message: "Tell us a bit more" } })} className={inputCls} placeholder="Tell us about your work and why this role is interesting." />
                </Field>
              </div>

              <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full" data-testid="careers-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit application <ArrowRight className="h-4 w-4" /></>}
              </button>
              <p className="mt-3 text-[11px] text-ink-400">By applying you agree to our hiring privacy notice.</p>
            </motion.form>
          </div>
        </div>
      </section>
    </main>
  );
}

const inputCls =
  "w-full rounded-lg bg-[#0F2B48] border border-white/[0.06] px-3.5 py-2.5 text-sm text-white placeholder:text-ink-400 focus:outline-none focus:border-cyan/50 focus:ring-2 focus:ring-cyan/20 transition";

function Field({ label, error, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-xs text-ink-200">{label}</span>
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-[11px] text-red-300">{error}</span>}
    </label>
  );
}
